import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useBalanceWithPoints } from "@apac/components/checkout";
import type { CreditCard } from "@b2bportal/card-api";
import type { Promotion } from "@b2bportal/hsbc-promotions-api";
import {
  AwardsEnum,
  CardCriteriaEnum,
  CriteriaEnum,
  type FixedAmount,
  type PayWithCardBin,
  type PayWithCardMinimum,
  type ProductCriteria,
  ProductCriteriaEnum,
} from "@b2bportal/hsbc-wallet-api";
import type { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getPointsEarnedPerDollar } from "@hsbc/portal";
import {
  selectCards,
  useGetWalletCreditEarnQuery,
  useListPaymentMethodsMutation,
  walletEarnSelector,
} from "@hsbc/wallet";
import { useIsPromotionApplicable } from "../../../promotions/useIsPromotionApplicable";

const incentivesStringMap = {
  [`${AwardsEnum.ByCardAmount}-${CardCriteriaEnum.PayWithCardBin}`]:
    "wallet.incentives.ByCardAmount-PayWithCardBin",
  [`${AwardsEnum.ByCardAmount}-${CardCriteriaEnum.PayWithCardMinimum}`]:
    "wallet.incentives.ByCardAmount-PayWithCardMinimum",

  [`${AwardsEnum.ByTotalAmount}-${ProductCriteriaEnum.IsAir}`]:
    "wallet.incentives.ByTotalAmount-IsAir",
  [`${AwardsEnum.ByTotalAmount}-${ProductCriteriaEnum.IsLodging}`]:
    "wallet.incentives.ByTotalAmount-IsLodging",
  [AwardsEnum.ByTotalAmount]: "wallet.incentives.ByTotalAmount",
  [AwardsEnum.FixedAmount]: "wallet.incentives.FixedAmount",
  validity: "wallet.incentives.validity",
};

export type MerchandisingAlertReturn = {
  earns: Array<{
    title: string;
    subtitle?: string;
  }>;
  premierMastercard?: {
    hasCard: boolean;
    isSelected: boolean;
    pointsEarn: string;
  };
  currentPromotion?: Promotion;
};

export const useMerchandisingAlert = (props: {
  selectedPayment?: CreditCard;
  product?: Product;
}): MerchandisingAlertReturn => {
  const { selectedPayment: card, product } = props;
  const { t, formatRewardsCurrency } = useI18nContext();
  const cards = useSelector(selectCards);
  const eligibleEarns = useSelector(walletEarnSelector);

  const [triggerListCards] = useListPaymentMethodsMutation({
    fixedCacheKey: "listCards",
  });
  const fetchEarn = useGetWalletCreditEarnQuery().refetch;
  const { remainingBalance } = useBalanceWithPoints();

  const {
    hasPremierMastercard,
    isCardApplicableForProduct,
    getCurrentPromotionForCard,
  } = useIsPromotionApplicable(product);
  const currentPromotion = getCurrentPromotionForCard(card);

  useEffect(() => {
    triggerListCards();
    fetchEarn();
  }, [triggerListCards, fetchEarn]);

  return useMemo(() => {
    const isSelectingPremierMastercard =
      card != null && isCardApplicableForProduct([card]);

    const premierDestinationEarns = Math.floor(
      getPointsEarnedPerDollar(currentPromotion) * remainingBalance.value
    );

    const premierMastercard = {
      hasCard: hasPremierMastercard,
      isSelected: isSelectingPremierMastercard,
      pointsEarn: formatRewardsCurrency({
        value: premierDestinationEarns,
        currency: "points",
      }),
    };

    if (!eligibleEarns || eligibleEarns.length === 0) {
      return {
        earns: [],
        premierMastercard,
        currentPromotion,
      };
    }

    const earns = eligibleEarns.map((e) => {
      let i18nKey = incentivesStringMap[e.awards.Awards];
      let title = "";
      let subtitle = "";
      switch (e.awards.Awards) {
        case AwardsEnum.ByTotalAmount: {
          const totalEarn = e.awards.percentage;
          const productCriteria = e.applicability.criterias.find(
            (c) =>
              c.Criteria === CriteriaEnum.ProductCriteria &&
              c.ProductCriteria === ProductCriteriaEnum.IsAir
          );
          if (productCriteria) {
            i18nKey = `${i18nKey}-${
              (productCriteria as ProductCriteria).ProductCriteria
            }`;
          }
          title = t(i18nKey, { value: totalEarn });
          subtitle = t(incentivesStringMap.validity);
          break;
        }
        case AwardsEnum.ByCardAmount: {
          const binCriteria = e.applicability.criterias.find(
            (c) =>
              c.Criteria === CriteriaEnum.CardCriteria &&
              c.CardCriteria === CardCriteriaEnum.PayWithCardBin
          ) as PayWithCardBin;
          const minSpendCriteria = e.applicability.criterias.find(
            (c) =>
              c.Criteria === CriteriaEnum.CardCriteria &&
              c.CardCriteria === CardCriteriaEnum.PayWithCardMinimum
          ) as PayWithCardMinimum;
          const validBins = binCriteria?.bin;
          const minSpend = minSpendCriteria?.minimumSpendSgd;
          if (minSpend !== undefined && card) {
            title = t(i18nKey, {
              lastFour: card.last4,
              value: e.awards.percentage,
            });
            subtitle = t(incentivesStringMap.validity);
          }
          if (
            validBins.includes(card.bin) ||
            cards.find((c) => validBins.includes(c.bin))
          ) {
            title = t(i18nKey, {
              value: e.awards.percentage,
            });
            subtitle = t(incentivesStringMap.validity);
          }
          break;
        }
        case AwardsEnum.FixedAmount: {
          title = t(i18nKey, {
            value: (e.awards as FixedAmount).amountSgd,
          });

          subtitle = t(incentivesStringMap.validity);
          break;
        }
      }
      return { title, subtitle };
    });

    return {
      earns,
      currentPromotion,
      premierMastercard,
    };
  }, [
    card,
    cards,
    currentPromotion,
    eligibleEarns,
    formatRewardsCurrency,
    hasPremierMastercard,
    isCardApplicableForProduct,
    remainingBalance.value,
    t,
  ]);
};
